import React, { useEffect, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import { Box, ButtonGroup, Tooltip, HStack, VStack, } from '@chakra-ui/react';
import Icon from '../../../components/content/icon';
import Button from '../../../components/inputs/button';
import FONTS from '../../../constants/fonts';
import COLORS from '../../../constants/colors';
import Text from '../../../components/content/text';
import { useTranslation } from 'react-i18next';
import { tKeys } from '../../../translations/keys';
export const Editor = ({ editor, className, handleSave, exportPDF, exportDOCX, event, }) => {
    const [isSaving, setIsSaving] = useState(false);
    const { t } = useTranslation();
    useEffect(() => {
        if (!editor)
            return;
        let timeoutId;
        const save = async () => {
            setIsSaving(true);
            await handleSave();
            setIsSaving(false);
        };
        const updateListener = editor.on('update', () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(save, 1500);
        });
        return () => {
            clearTimeout(timeoutId);
            updateListener.destroy();
        };
    }, [editor, handleSave]);
    const handleBoxClick = () => {
        if (editor) {
            editor.commands.focus(); // Focus at end of content
        }
    };
    return (<VStack spacing={6} width="100%" maxW="100%" mx="auto">
      {/* Title Section */}
      {/* <Box width="100%" bg="white" p={6} borderRadius="lg" shadow="md">
          <Text font={FONTS.T1.T32Px.Bold700.value} color={COLORS.Text.T500.value}>
            Meeting Minutes: {event?.name}
          </Text>
        </Box> */}

      {/* Toolbar */}
      <Box width="80vw%" bg="white" borderRadius="lg" shadow="md" position="sticky" top={4} zIndex={1}>
        <HStack spacing={4} p={4} borderBottomWidth="1px" borderColor="gray.200" justify="space-between" align="center">
          {/* Add new indentation buttons group */}
          <ButtonGroup size="sm" isAttached variant="outline" spacing={0}>
            <Tooltip label={t(tKeys.minutes.editor.toolbar.decrease_indent)} openDelay={500}>
              <Button kind="TRANSPARENT" onPress={() => editor.chain().focus().liftListItem('listItem').run()} size="COMPACT" borderWidth={1} borderColor="gray.300">
                <Icon name="indent-decreased" height={30} width={30} color="black"/>
              </Button>
            </Tooltip>
            <Tooltip label="Increase Indent" openDelay={500}>
              <Button kind="TRANSPARENT" onPress={() => editor.chain().focus().sinkListItem('listItem').run()} size="COMPACT" borderWidth={1} borderColor="gray.300">
                <Icon name="indent-increased" height={30} width={30} color="black"/>
              </Button>
            </Tooltip>
          </ButtonGroup>

          {/* Formatting buttons */}
          <ButtonGroup size="sm" isAttached variant="outline" spacing={0}>
            <Tooltip label="Heading 1" openDelay={500}>
              <Button kind={editor.isActive('heading', { level: 1 })
            ? 'PRIMARY'
            : 'TRANSPARENT'} onPress={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} size="COMPACT" borderWidth={1} borderColor={editor.isActive('orderedList') ? 'blue.500' : 'gray.300'}>
                <Icon name="header-1" height={30} width={30} color={editor.isActive('heading', { level: 1 }) ? 'white' : 'black'}/>
              </Button>
              {/* <IconButton
                aria-label="Heading 1"
              //   icon={<Icon name="heading-1" height={20} width={20} />}
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                isActive={editor.isActive('heading', { level: 1 })}
                colorScheme={editor.isActive('heading', { level: 1 }) ? 'blue' : 'gray'}
                borderRadius="md"
                size="md"
              /> */}
            </Tooltip>
            <Tooltip label="Heading 2" openDelay={500}>
              <Button kind={editor.isActive('heading', { level: 2 })
            ? 'PRIMARY'
            : 'TRANSPARENT'} onPress={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} size="COMPACT" borderWidth={1} borderColor={editor.isActive('orderedList') ? 'blue.500' : 'gray.300'}>
                <Icon name="header-2" height={30} width={30} color={editor.isActive('heading', { level: 2 }) ? 'white' : 'black'}/>
              </Button>
            </Tooltip>
            <Tooltip label="Heading 3" openDelay={500}>
              <Button kind={editor.isActive('heading', { level: 3 })
            ? 'PRIMARY'
            : 'TRANSPARENT'} onPress={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} size="COMPACT" borderWidth={1} borderColor={editor.isActive('orderedList') ? 'blue.500' : 'gray.300'}>
                <Icon name="header-3" height={30} width={30} color={editor.isActive('heading', { level: 3 }) ? 'white' : 'black'}/>
              </Button>
            </Tooltip>
          </ButtonGroup>

          <ButtonGroup size="sm" isAttached variant="outline" spacing={0}>
            <Tooltip label="Bold" openDelay={500}>
              <Button kind={editor.isActive('bold') ? 'PRIMARY' : 'TRANSPARENT'} onPress={() => editor.chain().focus().toggleBold().run()} size="COMPACT" borderWidth={1} borderColor={editor.isActive('orderedList') ? 'blue.500' : 'gray.300'}>
                <Icon name="bold" height={30} width={30} color={editor.isActive('bold') ? 'white' : 'black'}/>
              </Button>
            </Tooltip>
            <Tooltip label="Italic" openDelay={500}>
              <Button kind={editor.isActive('italic') ? 'PRIMARY' : 'TRANSPARENT'} onPress={() => editor.chain().focus().toggleItalic().run()} borderWidth={1} borderColor={editor.isActive('orderedList') ? 'blue.500' : 'gray.300'} size="COMPACT">
                <Icon name="italic" height={30} width={30} color={editor.isActive('italic') ? 'white' : 'black'}/>
              </Button>
            </Tooltip>
          </ButtonGroup>

          <ButtonGroup size="sm" isAttached variant="outline" spacing={0}>
            <Tooltip label="Bullet List" openDelay={500}>
              <Button kind={editor.isActive('bulletList') ? 'PRIMARY' : 'TRANSPARENT'} onPress={() => editor.chain().focus().toggleBulletList().run()} borderWidth={1} borderColor={editor.isActive('orderedList') ? 'blue.500' : 'gray.300'} size="COMPACT">
                <Icon name="editor-list-unorderd" height={30} width={30} color={editor.isActive('bulletList') ? 'white' : 'black'}/>
              </Button>
            </Tooltip>
            <Tooltip label="Numbered List" openDelay={500}>
              <Button kind={editor.isActive('orderedList') ? 'PRIMARY' : 'TRANSPARENT'} borderWidth={1} borderColor={editor.isActive('orderedList') ? 'blue.500' : 'gray.300'} onPress={() => editor.chain().focus().toggleOrderedList().run()} size="COMPACT">
                <Icon name="editor-list-ordered" height={30} width={30} color={editor.isActive('orderedList') ? 'white' : 'black'}/>
              </Button>
            </Tooltip>
          </ButtonGroup>

          {/* Update the export buttons section */}
          {/* <HStack spacing={4} align="center">
          <Text fontSize="sm" color={isSaving ? "blue.500" : "green.500"} fontWeight="medium">
            {isSaving ? '⏳ Saving...' : '✓ All changes saved'}
          </Text>
          <ButtonGroup size="md" spacing={3}>
            <Button
              isLoading={isSaving}
              onPress={() => handleSave()}
              colorScheme="blue"
            >
              Save
            </Button>
            <Button
              onPress={() => exportPDF()}
              leftIcon={<Icon name="file-pdf" height={20} width={20} />}
              colorScheme="red"
              variant="outline"
            >
              PDF
            </Button>
            <Button
              onPress={async () => await exportDOCX()}
              leftIcon={<Icon name="file-word" height={20} width={20} />}
              colorScheme="blue"
              variant="outline"
            >
              DOCX
            </Button>
          </ButtonGroup>
        </HStack> */}
        </HStack>
      </Box>

      {/* Editor content - updated styles with project fonts */}
      <Box width="100%" bg="white" p={8} borderRadius="lg" shadow="md" minH="600px" onClick={handleBoxClick} cursor="text" sx={{
            '.ProseMirror': {
                '&:focus': {
                    outline: 'none',
                },
                '&.prose': {
                    maxWidth: 'none',
                },
                'caret-color': 'blue.500',
                h1: {
                    ...FONTS.T1.T32Px.Bold700.value,
                    marginBottom: '0.7em',
                    color: COLORS.Text.T500.value,
                    borderBottom: '2px solid',
                    borderColor: COLORS.Line.value,
                    paddingBottom: '0.3em',
                    marginTop: '1.2em',
                    lineHeight: '1.3',
                },
                h2: {
                    ...FONTS.T1.T28Px.Bold700.value,
                    marginBottom: '0.6em',
                    marginTop: '1.2em',
                    color: COLORS.Text.T500.value,
                    lineHeight: '1.3',
                },
                h3: {
                    ...FONTS.T1.T20Px.Bold700.value,
                    marginBottom: '0.5em',
                    marginTop: '1.1em',
                    color: COLORS.Text.T500.value,
                    lineHeight: '1.4',
                },
                p: {
                    ...FONTS.T1.T16Px.Regular400.value,
                    marginBottom: '0.8em',
                    lineHeight: '1.6',
                    color: COLORS.Text.T400.value,
                    maxWidth: '65ch',
                    padding: '0.1em 0',
                },
                'ul, ol': {
                    marginLeft: '1.5em',
                    marginBottom: '1em',
                    marginTop: '0.8em',
                    '& ul, & ol': {
                        marginLeft: '1.5em',
                        marginBottom: '0.3em',
                        marginTop: '0.3em',
                    },
                },
                li: {
                    ...FONTS.T1.T16Px.Regular400.value,
                    marginBottom: '0.4em',
                    padding: '0.2em 0',
                    color: COLORS.Text.T400.value,
                    lineHeight: '1.2',
                    '& p': {
                        margin: '0.2em 0',
                    },
                },
                'ul > li': {
                    listStyleType: 'disc',
                    '& > ul > li': {
                        listStyleType: 'circle',
                        '& > ul > li': {
                            listStyleType: 'square',
                        },
                    },
                },
                'ol > li': {
                    listStyleType: 'decimal',
                    '& > ol > li': {
                        listStyleType: 'lower-alpha',
                        '& > ol > li': {
                            listStyleType: 'lower-roman',
                        },
                    },
                },
                strong: {
                    ...FONTS.T1.T16Px.SemiBold600.value,
                    color: COLORS.Text.T500.value,
                    lineHeight: '1.5',
                },
                em: {
                    ...FONTS.T1.T16Px.Regular400.value,
                    fontStyle: 'italic',
                    color: COLORS.Text.T400.value,
                    lineHeight: '1.5',
                },
                blockquote: {
                    borderLeft: '4px solid',
                    borderColor: COLORS.Line.value,
                    paddingLeft: '1em',
                    marginLeft: '0.5em',
                    marginBottom: '1em',
                    fontStyle: 'italic',
                    color: COLORS.Text.T300.value,
                },
                '& > * + *': {
                    marginTop: '0.5em',
                },
            },
        }}>
        <EditorContent editor={editor} className={className}/>
      </Box>

      {/* Updated export buttons section */}
      {/* <HStack spacing={4} align="center">
          <Text fontSize="sm" color={isSaving ? "blue.500" : "green.500"} fontWeight="medium">
            {isSaving ? '⏳ Saving...' : '✓ All changes saved'}
          </Text>
          <ButtonGroup size="md" spacing={3}>
            <Button
              isLoading={isSaving}
              onPress={() => handleSave()}
              colorScheme="blue"
            >
              Save
            </Button>
            <Button
              onPress={() => exportPDF()}
              leftIcon={<Icon name="file-pdf" height={20} width={20} />}
              colorScheme="red"
              variant="outline"
            >
              PDF
            </Button>
            <Button
              onPress={async () => await exportDOCX()}
              leftIcon={<Icon name="file-word" height={20} width={20} />}
              colorScheme="blue"
              variant="outline"
            >
              DOCX
            </Button>
          </ButtonGroup>
        </HStack> */}

      {/* Updated saving text */}
      <Text fontSize="sm" color={isSaving ? 'blue.500' : 'green.500'} fontWeight="medium">
        {isSaving
            ? t(tKeys.minutes.editor.saving)
            : t(tKeys.minutes.editor.saved)}
      </Text>
    </VStack>);
};
